import { useCallback, useState } from "react";

import { exportUrl, getAudit } from "../../api/freightAudit";
import { RunStatusBadge } from "../../components/Badges";
import { Button } from "../../components/Button";
import { buttonClass } from "../../components/buttonClass";
import { ErrorNotice, Notice, Skeleton } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { useAsync } from "../../hooks/useAsync";
import { Link } from "../../components/Link";
import { formatCount, formatDateTime, formatMoney, formatWeight, plural } from "../../lib/format";
import type { AuditRun, AuditStatus, ResultQuery, ReviewProgress } from "../../types/api";
import { ExceptionTable } from "./ExceptionTable";
import { DEFAULT_QUERY } from "./query";

function SummaryCards({ run, query, onFilter }: { run: AuditRun; query: ResultQuery; onFilter: (status: AuditStatus) => void }) {
  const summary = run.summary;
  if (!summary) return null;
  const cards: { status: AuditStatus; count: number; caption: string }[] = [
    { status: "PASS", count: summary.pass, caption: "Matched within tolerance" },
    { status: "REVIEW", count: summary.review, caption: "Need an operator's look" },
    { status: "HOLD", count: summary.hold, caption: "No matching shipment" },
  ];
  return (
    <div className="summary-cards">
      {cards.map((card) => {
        const active = query.status.length === 1 && query.status[0] === card.status;
        return (
          <button
            key={card.status}
            type="button"
            className={`stat stat--${card.status.toLowerCase()}${active ? " is-active" : ""}`}
            aria-pressed={active}
            onClick={() => onFilter(card.status)}
          >
            <span className="stat__value">{formatCount(card.count)}</span>
            <span className="stat__label">{card.status}</span>
            <span className="stat__caption">{card.caption}</span>
          </button>
        );
      })}
      <div className="stat stat--variance">
        <span className="stat__value">{formatMoney(summary.potential_positive_variance)}</span>
        <span className="stat__label">Positive variance flagged</span>
        <span className="stat__caption">Billed above quote on flagged rows. Not a confirmed overcharge.</span>
      </div>
    </div>
  );
}

function ReviewProgressBar({ progress }: { progress: ReviewProgress | null }) {
  if (!progress || progress.exceptions === 0) return null;
  const segments = [
    { key: "approved", label: "Approved", value: progress.approved },
    { key: "dispute", label: "Dispute", value: progress.dispute },
    { key: "ignored", label: "Ignored", value: progress.ignored },
  ];
  const percent = Math.round((progress.reviewed / progress.exceptions) * 100);
  return (
    <div className="progress-panel">
      <p className="progress-panel__title">
        <strong>
          {formatCount(progress.reviewed)} of {plural(progress.exceptions, "exception")}
        </strong>{" "}
        reviewed
      </p>
      <div
        className="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.exceptions}
        aria-valuenow={progress.reviewed}
        aria-valuetext={`${percent}% reviewed`}
      >
        {segments.map((segment) =>
          segment.value ? (
            <span
              key={segment.key}
              className={`progress__segment progress__segment--${segment.key}`}
              style={{ width: `${(segment.value / progress.exceptions) * 100}%` }}
            />
          ) : null,
        )}
      </div>
      <ul className="progress-legend">
        {segments.map((segment) => (
          <li key={segment.key} className={`progress-legend__item progress-legend__item--${segment.key}`}>
            {segment.label} {formatCount(segment.value)}
          </li>
        ))}
        <li className="progress-legend__item">Unreviewed {formatCount(progress.unreviewed)}</li>
      </ul>
    </div>
  );
}

function FailedRun({ run }: { run: AuditRun }) {
  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <div>
          <p className="eyebrow">FreightAudit</p>
          <h1>Audit {run.reference}</h1>
          <p className="muted">
            <RunStatusBadge status={run.status} /> {formatDateTime(run.created_at)}
          </p>
        </div>
      </header>
      <div className="notice notice--error" role="alert">
        <Icon name="alert" size={18} />
        <div className="notice__body">
          <p className="notice__title">This audit didn&apos;t complete.</p>
          <p>{run.error?.message ?? "The audit stopped before it produced results."}</p>
        </div>
      </div>
      <Link to="/" className={buttonClass("primary")}>
        Start a new audit
      </Link>
    </div>
  );
}

export function AuditDashboard({ auditId }: { auditId: string }) {
  const audit = useAsync((signal) => getAudit(auditId, signal), auditId);
  const [query, setQuery] = useState<ResultQuery>(DEFAULT_QUERY);
  const onQueryChange = useCallback((next: ResultQuery) => setQuery(next), []);
  const run = audit.data;

  if (!run) {
    return audit.error ? (
      <div className="dashboard">
        <ErrorNotice error={audit.error} onRetry={audit.error.status === 404 ? undefined : audit.reload} />
        <Link to="/audits" className={buttonClass("secondary")}>
          Back to history
        </Link>
      </div>
    ) : (
      <Skeleton lines={8} label="Loading audit" />
    );
  }
  if (run.status === "FAILED") return <FailedRun run={run} />;
  if (run.status !== "COMPLETED") {
    return (
      <Notice title="This audit is still running.">
        <Button size="sm" onClick={audit.reload}>
          Check again
        </Button>
      </Notice>
    );
  }

  function filterStatus(status: AuditStatus) {
    const active = query.status.length === 1 && query.status[0] === status;
    setQuery({
      ...query,
      view: status === "PASS" && !active ? "all" : query.view === "all" && status !== "PASS" ? "exceptions" : query.view,
      status: active ? [] : [status],
      page: 1,
    });
  }

  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <div>
          <p className="eyebrow">FreightAudit</p>
          <h1>Audit {run.reference}</h1>
          <p className="dashboard__meta">
            <strong>{plural(run.invoice_rows, "invoice")} analyzed</strong>
            <span>
              {run.shipment_filename ?? "API request"} vs {run.invoice_filename ?? "API request"}
            </span>
            <span>{formatDateTime(run.created_at)}</span>
          </p>
          <p className="dashboard__settings">
            Money tolerance {formatMoney(run.settings.money_tolerance)} · Weight tolerance{" "}
            {formatWeight(run.settings.weight_tolerance)}
            {run.expires_at ? <> · Kept until {formatDateTime(run.expires_at)}</> : null}
          </p>
        </div>
        <div className="dashboard__actions">
          <a className={buttonClass("secondary")} href={exportUrl(run.id)} download>
            <Icon name="download" /> Export workbook
          </a>
          <Link to="/" className={buttonClass("primary")}>
            <Icon name="plus" /> New audit
          </Link>
        </div>
      </header>

      <SummaryCards run={run} query={query} onFilter={filterStatus} />
      <ReviewProgressBar progress={run.review_progress} />
      <ExceptionTable run={run} query={query} onQueryChange={onQueryChange} onReviewed={audit.reload} />
    </div>
  );
}
